import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixEcomV1LocalDeliveryOption from './ecom-v1-local-delivery-option-local-delivery-options.http.js';
// @ts-ignore
import { transformSDKAddressToRESTAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface LocalDeliveryOption extends LocalDeliveryOptionConfigOneOf {
  zipConfig?: ZipCodeConfig;
  radiusConfig?: RadiusConfig;
  customAreaConfig?: CustomAreaConfig;
  cityConfig?: CityConfig;
  /**
   * LocalDeliveryOptionId - unique identifier of local delivery option
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Area name of Local Delivery Option
   * @maxLength 100
   */
  areaName?: string | null;
  /**
   * Flat price of local delivery
   * @format DECIMAL_VALUE
   * @deprecated Flat price of local delivery
   * @targetRemovalDate 2025-01-12
   */
  rate?: string | null;
  /**
   * Estimated delivery time description text
   * @maxLength 40
   */
  estimatedDeliveryTime?: string | null;
  /**
   * Minimum order total to be eligible for free delivery
   * @deprecated Minimum order total to be eligible for free delivery
   * @targetRemovalDate 2025-01-12
   */
  minimumFreeDeliveryOrderAmount?: number | null;
  /**
   * List of countries with subdivisions
   * @maxSize 100
   */
  destinations?: Destination[];
  /**
   * Database revision of LocalDeliveryOption entity. Incremented on each modification
   * @readonly
   */
  revision?: string | null;
  scheduledDeliveryConfig?: ScheduledDelivery;
  /**
   * at runtime for a given shipment input, up to one rate (price) should be returned in an option. If more than one rate is valid then we return the lowest one.
   * @maxSize 50
   */
  rates?: ConditionalRates[];
  /**
   * Date and time the LocalDeliveryOption was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the LocalDeliveryOption was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * This local delivery is active for the following delivery regions.
   * @maxSize 50
   * @format GUID
   */
  deliveryRegionIds?: string[];
}

/** @oneof */
export interface LocalDeliveryOptionConfigOneOf {
  zipConfig?: ZipCodeConfig;
  radiusConfig?: RadiusConfig;
  customAreaConfig?: CustomAreaConfig;
  cityConfig?: CityConfig;
}

export interface Destination {
  /**
   * Country ISO code
   * @format COUNTRY
   */
  code?: string;
  /**
   * List of subdivisions, empty means all
   * @maxSize 200
   */
  subdivisions?: string[];
}

export interface ZipCodeConfig {
  /**
   * List of ZipCodes and ZipCode regexes. Examples: 10001, 10002 or 1000*
   * @minLength 1
   * @maxLength 300
   */
  zipCodes?: string[];
}

export interface RadiusConfig {
  /** Delivery-from address */
  address?: LocalDeliveryAddress;
  /**
   * Delivery circle's radius
   * @min 0.1
   * @max 50
   */
  radius?: number;
  /**
   * Radius' unit, taken from site properties
   * @readonly
   */
  lengthUnit?: LengthUnitWithLiterals;
}

export interface LocalDeliveryAddress {
  /**
   * A string containing the human-readable address of this location
   * @maxLength 400
   */
  formatted?: string | null;
  /** coordinates of the physical address */
  location?: AddressLocation;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export enum LengthUnit {
  /** Length unit can't be classified, due to an error */
  UNSPECIFIED_LENGTH_UNIT = 'UNSPECIFIED_LENGTH_UNIT',
  /** Kilometers */
  KM = 'KM',
  /** Miles */
  MI = 'MI',
}

/** @enumType */
export type LengthUnitWithLiterals =
  | LengthUnit
  | 'UNSPECIFIED_LENGTH_UNIT'
  | 'KM'
  | 'MI';

export interface CustomAreaConfig {
  /**
   * Geocodes of the polygon defining the delivery area.
   * @maxSize 300
   */
  geocodes?: AddressLocation[];
}

export interface CityConfig {
  /**
   * List of the cities for delivery
   * @minSize 1
   * @maxSize 50
   */
  cityItems?: CityItem[];
}

export interface CityItem {
  /**
   * Name of the city
   * @minLength 1
   * @maxLength 100
   */
  cityName?: string;
  /**
   * Subdivision code of the city
   * @format SUBDIVISION
   */
  subdivision?: string | null;
  /**
   * Country code of the city
   * @format COUNTRY
   */
  country?: string;
  /**
   * Unique code of the city, calculated by Wix
   * @maxLength 100
   * @readonly
   */
  cityKey?: string | null;
}

export interface ScheduledDelivery {
  /**
   * Days config which contain days when delivery is enabled with it's time slots
   * @deprecated Days config which contain days when delivery is enabled with it's time slots
   * @replacedBy time_windows
   * @targetRemovalDate 2025-01-01
   */
  days?: Days;
  /**
   * Amount of days before first delivery option date
   * @max 30
   */
  handlingDays?: number | null;
  /** Cut off time after which first delivery option would be for next day */
  cutOffTime?: TimePoint;
  /**
   * availability times configuration.
   * @maxSize 100
   */
  timeWindows?: TimeWindow[];
}

export interface Days {
  monday?: DaySlot;
  tuesday?: DaySlot;
  wednesday?: DaySlot;
  thursday?: DaySlot;
  friday?: DaySlot;
  saturday?: DaySlot;
  sunday?: DaySlot;
}

export interface DaySlot {
  /** Shows whether this day slot is enabled for delivery */
  enabledForDelivery?: boolean | null;
  /**
   * Time slots for scheduled delivery during day
   * @maxSize 8
   */
  timeSlots?: TimeSlot[];
}

export interface TimeSlot {
  /** Starting time point of time slot */
  from?: TimePoint;
  /** End time point of time slot */
  to?: TimePoint;
}

export interface TimePoint {
  /**
   * Hours in format 0-23
   * @max 23
   */
  hours?: number | null;
  /**
   * Minutes 0-59
   * @max 59
   */
  minutes?: number | null;
}

export interface TimeWindow {
  /** The day of week this availability relates to. */
  day?: DayOfWeekWithLiterals;
  /** The start time in time of day representation. */
  start?: TimeOfDay;
  /** The end time in time of day representation. */
  end?: TimeOfDay;
  /** Whether this timeWindow availability is enabled. */
  enabled?: boolean | null;
}

export enum DayOfWeek {
  /** Monday. */
  MON = 'MON',
  /** Tuesday. */
  TUE = 'TUE',
  /** Wednesday. */
  WED = 'WED',
  /** Thursday. */
  THU = 'THU',
  /** Friday. */
  FRI = 'FRI',
  /** Saturday. */
  SAT = 'SAT',
  /** Sunday. */
  SUN = 'SUN',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'MON'
  | 'TUE'
  | 'WED'
  | 'THU'
  | 'FRI'
  | 'SAT'
  | 'SUN';

export interface TimeOfDay {
  /**
   * Hours
   * @max 23
   */
  hours?: number;
  /**
   * Minutes
   * @max 59
   */
  minutes?: number;
}

export interface ConditionalRates {
  /**
   * there is an AND logic between all the conditions. Empty conditions means true.
   * The amount of the rate that will be returned if all conditions are met.
   * @decimalValue options { gte:0, lte:999999999, maxScale:3 }
   */
  amount?: string;
  /**
   * For example: weight > 0 and weight <= 10
   * @maxSize 10
   */
  conditions?: Condition[];
  /** When this flag is set to true, multiply the amount by the number of line items passed on the request. */
  multiplyByQuantity?: boolean;
}

export interface Condition {
  type?: ConditionTypeWithLiterals;
  /**
   * The value in respective to the condition type
   * Weight values should be in the same weight units of the store: KG / LB
   * Total price is according to the store currency
   * Quantity of items should be integers
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  value?: string;
  /** Logical operator. */
  operator?: LogicalOperatorWithLiterals;
}

export enum ConditionType {
  BY_TOTAL_WEIGHT = 'BY_TOTAL_WEIGHT',
  BY_TOTAL_PRICE = 'BY_TOTAL_PRICE',
  BY_TOTAL_QUANTITY = 'BY_TOTAL_QUANTITY',
}

/** @enumType */
export type ConditionTypeWithLiterals =
  | ConditionType
  | 'BY_TOTAL_WEIGHT'
  | 'BY_TOTAL_PRICE'
  | 'BY_TOTAL_QUANTITY';

export enum LogicalOperator {
  EQ = 'EQ',
  GT = 'GT',
  GTE = 'GTE',
  LT = 'LT',
  LTE = 'LTE',
}

/** @enumType */
export type LogicalOperatorWithLiterals =
  | LogicalOperator
  | 'EQ'
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE';

export interface CreateLocalDeliveryOptionRequest {
  localDeliveryOption: LocalDeliveryOption;
}

export interface CreateLocalDeliveryOptionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface GetLocalDeliveryOptionRequest {
  /**
   * @maxLength 50
   * @deprecated
   * @targetRemovalDate 2025-01-12
   */
  _id?: string;
  /** @maxLength 50 */
  localDeliveryOptionId: string;
}

export interface GetLocalDeliveryOptionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface ListLocalDeliveryOptionsRequest {
  /** @format GUID */
  externalId: string;
}

export interface ListLocalDeliveryOptionsResponse {
  localDeliveryOptions?: LocalDeliveryOption[];
}

export interface QueryLocalDeliveryOptionsRequest {
  /** WQL expression */
  query: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 5
   */
  sort?: Sorting[];
  /**
   * Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned.
   * @maxSize 100
   * @maxLength 20
   */
  fields?: string[];
  /**
   * Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned.
   * @maxSize 100
   * @maxLength 20
   */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryLocalDeliveryOptionsResponse {
  /** Retrieved local delivery options. */
  localDeliveryOptions?: LocalDeliveryOption[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpdateLocalDeliveryOptionRequest {
  /** LocalDeliveryOption to be updated, may be partial. */
  localDeliveryOption: LocalDeliveryOption;
}

export interface UpdateLocalDeliveryOptionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface DeleteLocalDeliveryOptionRequest {
  /**
   * @maxLength 50
   * @deprecated
   * @targetRemovalDate 2025-01-12
   */
  _id?: string;
  /** @maxLength 50 */
  localDeliveryOptionId: string;
}

export interface DeleteLocalDeliveryOptionResponse {}

export interface BulkCreateLocalDeliveryOptionRequest {
  /**
   * @minSize 1
   * @maxSize 100
   */
  localDeliveryOptions: LocalDeliveryOption[];
}

export interface BulkCreateLocalDeliveryOptionResponse {
  localDeliveryOptions?: LocalDeliveryOption[];
  errors?: LocalDeliveryOptionError[];
}

export interface LocalDeliveryOptionError {
  _id?: string;
  error?: string;
}

export interface BulkUpdateLocalDeliveryOptionRequest {
  /**
   * @minSize 1
   * @maxSize 100
   */
  localDeliveryOptions: LocalDeliveryOption[];
}

export interface BulkUpdateLocalDeliveryOptionResponse {
  localDeliveryOptions?: LocalDeliveryOption[];
  errors?: LocalDeliveryOptionError[];
}

export interface BulkDeleteLocalDeliveryOptionRequest {
  /**
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  ids: string[];
}

export interface BulkDeleteLocalDeliveryOptionResponse {}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface AddDeliveryRegionRequest {
  /** @format GUID */
  deliveryRegionId: string;
  /** @format GUID */
  localDeliveryOptionId: string;
  /** Revision of the PickupLocation */
  revision?: string | null;
}

export interface AddDeliveryRegionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface RemoveDeliveryRegionRequest {
  /** @format GUID */
  deliveryRegionId: string;
  /** @format GUID */
  localDeliveryOptionId: string;
  /** Revision of the PickupLocation */
  revision?: string | null;
}

export interface RemoveDeliveryRegionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateLocalDeliveryOptionApplicationErrors = {
  code?: 'LIMIT_OF_OPTIONS_PER_EXTERNAL_ID_EXCEEDED';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkCreateLocalDeliveryOptionApplicationErrors = {
  code?: 'LIMIT_OF_OPTIONS_PER_DELIVERY_REGION_EXCEEDED';
  description?: string;
  data?: Record<string, any>;
};

/** @public
 * @documentationMaturity preview
 * @requiredField localDeliveryOption
 * @requiredField localDeliveryOption.areaName
 * @requiredField localDeliveryOption.config.cityConfig.cityItems.cityName
 * @requiredField localDeliveryOption.config.cityConfig.cityItems.country
 * @requiredField localDeliveryOption.config.radiusConfig.address
 * @requiredField localDeliveryOption.config.radiusConfig.address.formatted
 * @requiredField localDeliveryOption.config.radiusConfig.address.location
 * @requiredField localDeliveryOption.rate
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.handlingDays
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_CREATE
 * @applicableIdentity APP
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.CreateLocalDeliveryOption
 */
export async function createLocalDeliveryOption(
  localDeliveryOption: NonNullablePaths<
    LocalDeliveryOption,
    | `areaName`
    | `cityConfig.cityItems.${number}.cityName`
    | `cityConfig.cityItems.${number}.country`
    | `radiusConfig.address`
    | `radiusConfig.address.formatted`
    | `radiusConfig.address.location`
    | `rate`
    | `scheduledDeliveryConfig.days.friday`
    | `scheduledDeliveryConfig.days.friday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.monday`
    | `scheduledDeliveryConfig.days.monday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.saturday`
    | `scheduledDeliveryConfig.days.saturday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.sunday`
    | `scheduledDeliveryConfig.days.sunday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.thursday`
    | `scheduledDeliveryConfig.days.thursday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.tuesday`
    | `scheduledDeliveryConfig.days.tuesday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.wednesday`
    | `scheduledDeliveryConfig.days.wednesday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.handlingDays`,
    8
  >
): Promise<
  NonNullablePaths<
    LocalDeliveryOption,
    | `zipConfig.zipCodes`
    | `radiusConfig.radius`
    | `radiusConfig.lengthUnit`
    | `customAreaConfig.geocodes`
    | `cityConfig.cityItems`
    | `cityConfig.cityItems.${number}.cityName`
    | `cityConfig.cityItems.${number}.country`
    | `destinations`
    | `destinations.${number}.code`
    | `scheduledDeliveryConfig.days.monday.timeSlots`
    | `scheduledDeliveryConfig.timeWindows`
    | `scheduledDeliveryConfig.timeWindows.${number}.day`
    | `scheduledDeliveryConfig.timeWindows.${number}.start.hours`
    | `scheduledDeliveryConfig.timeWindows.${number}.start.minutes`
    | `rates`
    | `rates.${number}.amount`
    | `rates.${number}.multiplyByQuantity`
    | `deliveryRegionIds`,
    6
  > & {
    __applicationErrorsType?: CreateLocalDeliveryOptionApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      localDeliveryOption: localDeliveryOption,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'localDeliveryOption.radiusConfig.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.createLocalDeliveryOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOption.radiusConfig.address' }],
        },
      ])
    )?.localDeliveryOption!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { localDeliveryOption: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['localDeliveryOption']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/** @public
 * @documentationMaturity preview
 * @requiredField localDeliveryOptionId
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_READ
 * @applicableIdentity APP
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.GetLocalDeliveryOption
 */
export async function getLocalDeliveryOption(
  localDeliveryOptionId: string,
  options?: GetLocalDeliveryOptionOptions
): Promise<
  NonNullablePaths<
    LocalDeliveryOption,
    | `zipConfig.zipCodes`
    | `radiusConfig.radius`
    | `radiusConfig.lengthUnit`
    | `customAreaConfig.geocodes`
    | `cityConfig.cityItems`
    | `cityConfig.cityItems.${number}.cityName`
    | `cityConfig.cityItems.${number}.country`
    | `destinations`
    | `destinations.${number}.code`
    | `scheduledDeliveryConfig.days.monday.timeSlots`
    | `scheduledDeliveryConfig.timeWindows`
    | `scheduledDeliveryConfig.timeWindows.${number}.day`
    | `scheduledDeliveryConfig.timeWindows.${number}.start.hours`
    | `scheduledDeliveryConfig.timeWindows.${number}.start.minutes`
    | `rates`
    | `rates.${number}.amount`
    | `rates.${number}.multiplyByQuantity`
    | `deliveryRegionIds`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    localDeliveryOptionId: localDeliveryOptionId,
    id: options?._id,
  });

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.getLocalDeliveryOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOption.radiusConfig.address' }],
        },
      ])
    )?.localDeliveryOption!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          localDeliveryOptionId: '$[0]',
          id: '$[1]._id',
        },
        singleArgumentUnchanged: false,
      },
      ['localDeliveryOptionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetLocalDeliveryOptionOptions {
  /**
   * @maxLength 50
   * @deprecated
   * @targetRemovalDate 2025-01-12
   */
  _id?: string;
}

/** @public
 * @documentationMaturity preview
 * @requiredField externalId
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_READ
 * @applicableIdentity APP
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.ListLocalDeliveryOptions
 * @deprecated method is deprecated due to the fact that external_id is deprecated
 * @replacedBy ListLocalDeliveryOptionsByDeliveryRegion
 * @targetRemovalDate 2024-11-01
 */
export async function listLocalDeliveryOptions(
  externalId: string
): Promise<
  NonNullablePaths<
    ListLocalDeliveryOptionsResponse,
    | `localDeliveryOptions`
    | `localDeliveryOptions.${number}.radiusConfig.radius`
    | `localDeliveryOptions.${number}.radiusConfig.lengthUnit`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    externalId: externalId,
  });

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.listLocalDeliveryOptions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOptions.radiusConfig.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { externalId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['externalId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of local delivery options, given the provided paging, filtering, and sorting.
 *
 * To learn about working with _Query_ endpoints, see
 * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language),
 * [Sorting and Paging](https://dev.wix.com/api/rest/getting-started/sorting-and-paging),
 * and [Field Projection](https://dev.wix.com/api/rest/getting-started/field-projection).
 * @public
 * @documentationMaturity preview
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_READ
 * @applicableIdentity APP
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.QueryLocalDeliveryOptions
 */
export function queryLocalDeliveryOptions(): LocalDeliveryOptionsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    LocalDeliveryOption,
    'CURSOR',
    QueryLocalDeliveryOptionsRequest,
    QueryLocalDeliveryOptionsResponse
  >({
    func: async (payload: QueryLocalDeliveryOptionsRequest) => {
      const reqOpts =
        ambassadorWixEcomV1LocalDeliveryOption.queryLocalDeliveryOptions(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryLocalDeliveryOptionsRequest['query']) => {
      const args = [query, {}] as [
        QueryLocalDeliveryOptionsRequest['query'],
        {}
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryLocalDeliveryOptionsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTAddressToSDKAddress,
            paths: [{ path: 'localDeliveryOptions.radiusConfig.address' }],
          },
        ])
      );

      return {
        items: transformedData?.localDeliveryOptions,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface LocalDeliveryOptionsQueryResult extends QueryCursorResult {
  items: LocalDeliveryOption[];
  query: LocalDeliveryOptionsQueryBuilder;
  next: () => Promise<LocalDeliveryOptionsQueryResult>;
  prev: () => Promise<LocalDeliveryOptionsQueryResult>;
}

export interface LocalDeliveryOptionsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_id'
      | 'areaName'
      | 'estimatedDeliveryTime'
      | 'deliveryRegionIds',
    value: any
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_id'
      | 'areaName'
      | 'estimatedDeliveryTime'
      | 'deliveryRegionIds',
    value: any
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName: '_id' | 'areaName' | 'estimatedDeliveryTime',
    value: any
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName: '_id' | 'areaName' | 'estimatedDeliveryTime',
    value: any
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName: '_id' | 'areaName' | 'estimatedDeliveryTime',
    value: any
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName: '_id' | 'areaName' | 'estimatedDeliveryTime',
    value: any
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName: '_id' | 'areaName' | 'estimatedDeliveryTime',
    value: string
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName:
      | '_id'
      | 'areaName'
      | 'estimatedDeliveryTime'
      | 'deliveryRegionIds',
    value: any[]
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasAll: (
    propertyName: 'deliveryRegionIds',
    value: any[]
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_id'
      | 'areaName'
      | 'estimatedDeliveryTime'
      | 'deliveryRegionIds',
    value: any
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_id'
      | 'areaName'
      | 'estimatedDeliveryTime'
      | 'deliveryRegionIds',
    value: boolean
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => LocalDeliveryOptionsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => LocalDeliveryOptionsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<LocalDeliveryOptionsQueryResult>;
}

/**
 * @hidden
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.QueryLocalDeliveryOptions
 * @requiredField query
 */
export async function typedQueryLocalDeliveryOptions(
  query: LocalDeliveryOptionQuery
): Promise<
  NonNullablePaths<
    QueryLocalDeliveryOptionsResponse,
    | `localDeliveryOptions`
    | `localDeliveryOptions.${number}.radiusConfig.radius`
    | `localDeliveryOptions.${number}.radiusConfig.lengthUnit`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.queryLocalDeliveryOptions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOptions.radiusConfig.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface LocalDeliveryOptionQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['deliveryRegionIds'];
      operators: ['$hasAll', '$hasSome'];
      sort: 'NONE';
    },
    {
      fields: ['_id', 'areaName', 'estimatedDeliveryTime'];
      operators: '*';
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  LocalDeliveryOption,
  LocalDeliveryOptionQuerySpec
>;
export type LocalDeliveryOptionQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section). 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      LocalDeliveryOption,
      LocalDeliveryOptionQuerySpec,
      LocalDeliveryOptionQuery
    >(),
  },
};

/** @param _id - LocalDeliveryOptionId - unique identifier of local delivery option
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField localDeliveryOption
 * @requiredField localDeliveryOption.config.cityConfig.cityItems.cityName
 * @requiredField localDeliveryOption.config.cityConfig.cityItems.country
 * @requiredField localDeliveryOption.config.radiusConfig.address
 * @requiredField localDeliveryOption.config.radiusConfig.address.formatted
 * @requiredField localDeliveryOption.config.radiusConfig.address.location
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.handlingDays
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_UPDATE
 * @applicableIdentity APP
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.UpdateLocalDeliveryOption
 */
export async function updateLocalDeliveryOption(
  _id: string,
  localDeliveryOption: NonNullablePaths<
    UpdateLocalDeliveryOption,
    | `cityConfig.cityItems.${number}.cityName`
    | `cityConfig.cityItems.${number}.country`
    | `radiusConfig.address`
    | `radiusConfig.address.formatted`
    | `radiusConfig.address.location`
    | `scheduledDeliveryConfig.days.friday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.monday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.saturday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.sunday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.thursday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.tuesday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.wednesday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.handlingDays`,
    8
  >
): Promise<
  NonNullablePaths<
    LocalDeliveryOption,
    | `zipConfig.zipCodes`
    | `radiusConfig.radius`
    | `radiusConfig.lengthUnit`
    | `customAreaConfig.geocodes`
    | `cityConfig.cityItems`
    | `cityConfig.cityItems.${number}.cityName`
    | `cityConfig.cityItems.${number}.country`
    | `destinations`
    | `destinations.${number}.code`
    | `scheduledDeliveryConfig.days.monday.timeSlots`
    | `scheduledDeliveryConfig.timeWindows`
    | `scheduledDeliveryConfig.timeWindows.${number}.day`
    | `scheduledDeliveryConfig.timeWindows.${number}.start.hours`
    | `scheduledDeliveryConfig.timeWindows.${number}.start.minutes`
    | `rates`
    | `rates.${number}.amount`
    | `rates.${number}.multiplyByQuantity`
    | `deliveryRegionIds`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      localDeliveryOption: { ...localDeliveryOption, id: _id },
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'localDeliveryOption.radiusConfig.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.updateLocalDeliveryOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOption.radiusConfig.address' }],
        },
      ])
    )?.localDeliveryOption!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { localDeliveryOption: '$[1]' },
        explicitPathsToArguments: { 'localDeliveryOption.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'localDeliveryOption']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateLocalDeliveryOption {
  zipConfig?: ZipCodeConfig;
  radiusConfig?: RadiusConfig;
  customAreaConfig?: CustomAreaConfig;
  cityConfig?: CityConfig;
  /**
   * LocalDeliveryOptionId - unique identifier of local delivery option
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Area name of Local Delivery Option
   * @maxLength 100
   */
  areaName?: string | null;
  /**
   * Flat price of local delivery
   * @format DECIMAL_VALUE
   * @deprecated Flat price of local delivery
   * @targetRemovalDate 2025-01-12
   */
  rate?: string | null;
  /**
   * Estimated delivery time description text
   * @maxLength 40
   */
  estimatedDeliveryTime?: string | null;
  /**
   * Minimum order total to be eligible for free delivery
   * @deprecated Minimum order total to be eligible for free delivery
   * @targetRemovalDate 2025-01-12
   */
  minimumFreeDeliveryOrderAmount?: number | null;
  /**
   * List of countries with subdivisions
   * @maxSize 100
   */
  destinations?: Destination[];
  /**
   * Database revision of LocalDeliveryOption entity. Incremented on each modification
   * @readonly
   */
  revision?: string | null;
  scheduledDeliveryConfig?: ScheduledDelivery;
  /**
   * at runtime for a given shipment input, up to one rate (price) should be returned in an option. If more than one rate is valid then we return the lowest one.
   * @maxSize 50
   */
  rates?: ConditionalRates[];
  /**
   * Date and time the LocalDeliveryOption was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the LocalDeliveryOption was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * This local delivery is active for the following delivery regions.
   * @maxSize 50
   * @format GUID
   */
  deliveryRegionIds?: string[];
}

/** @public
 * @documentationMaturity preview
 * @requiredField localDeliveryOptionId
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_DELETE
 * @applicableIdentity APP
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.DeleteLocalDeliveryOption
 */
export async function deleteLocalDeliveryOption(
  localDeliveryOptionId: string,
  options?: DeleteLocalDeliveryOptionOptions
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    localDeliveryOptionId: localDeliveryOptionId,
    id: options?._id,
  });

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.deleteLocalDeliveryOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          localDeliveryOptionId: '$[0]',
          id: '$[1]._id',
        },
        singleArgumentUnchanged: false,
      },
      ['localDeliveryOptionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteLocalDeliveryOptionOptions {
  /**
   * @maxLength 50
   * @deprecated
   * @targetRemovalDate 2025-01-12
   */
  _id?: string;
}

/** @public
 * @documentationMaturity preview
 * @requiredField localDeliveryOptions
 * @requiredField localDeliveryOptions.config.cityConfig.cityItems.cityName
 * @requiredField localDeliveryOptions.config.cityConfig.cityItems.country
 * @requiredField localDeliveryOptions.config.radiusConfig.address
 * @requiredField localDeliveryOptions.config.radiusConfig.address.formatted
 * @requiredField localDeliveryOptions.config.radiusConfig.address.location
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.handlingDays
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_CREATE
 * @applicableIdentity APP
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.BulkCreateLocalDeliveryOption
 */
export async function bulkCreateLocalDeliveryOption(
  localDeliveryOptions: NonNullablePaths<
    LocalDeliveryOption,
    | `cityConfig.cityItems.${number}.cityName`
    | `cityConfig.cityItems.${number}.country`
    | `radiusConfig.address`
    | `radiusConfig.address.formatted`
    | `radiusConfig.address.location`
    | `scheduledDeliveryConfig.days.friday`
    | `scheduledDeliveryConfig.days.friday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.monday`
    | `scheduledDeliveryConfig.days.monday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.saturday`
    | `scheduledDeliveryConfig.days.saturday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.sunday`
    | `scheduledDeliveryConfig.days.sunday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.thursday`
    | `scheduledDeliveryConfig.days.thursday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.tuesday`
    | `scheduledDeliveryConfig.days.tuesday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.wednesday`
    | `scheduledDeliveryConfig.days.wednesday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.handlingDays`,
    8
  >[]
): Promise<
  NonNullablePaths<
    BulkCreateLocalDeliveryOptionResponse,
    | `localDeliveryOptions`
    | `localDeliveryOptions.${number}.radiusConfig.radius`
    | `localDeliveryOptions.${number}.radiusConfig.lengthUnit`
    | `errors`
    | `errors.${number}._id`
    | `errors.${number}.error`,
    5
  > & {
    __applicationErrorsType?: BulkCreateLocalDeliveryOptionApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      localDeliveryOptions: localDeliveryOptions,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'localDeliveryOptions.radiusConfig.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.bulkCreateLocalDeliveryOption(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOptions.radiusConfig.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { localDeliveryOptions: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['localDeliveryOptions']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/** @public
 * @documentationMaturity preview
 * @requiredField localDeliveryOptions
 * @requiredField localDeliveryOptions.config.cityConfig.cityItems.cityName
 * @requiredField localDeliveryOptions.config.cityConfig.cityItems.country
 * @requiredField localDeliveryOptions.config.radiusConfig.address
 * @requiredField localDeliveryOptions.config.radiusConfig.address.formatted
 * @requiredField localDeliveryOptions.config.radiusConfig.address.location
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.handlingDays
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_UPDATE
 * @applicableIdentity APP
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.BulkUpdateLocalDeliveryOption
 */
export async function bulkUpdateLocalDeliveryOption(
  localDeliveryOptions: NonNullablePaths<
    LocalDeliveryOption,
    | `cityConfig.cityItems.${number}.cityName`
    | `cityConfig.cityItems.${number}.country`
    | `radiusConfig.address`
    | `radiusConfig.address.formatted`
    | `radiusConfig.address.location`
    | `scheduledDeliveryConfig.days.friday`
    | `scheduledDeliveryConfig.days.friday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.friday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.monday`
    | `scheduledDeliveryConfig.days.monday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.monday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.saturday`
    | `scheduledDeliveryConfig.days.saturday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.saturday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.sunday`
    | `scheduledDeliveryConfig.days.sunday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.sunday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.thursday`
    | `scheduledDeliveryConfig.days.thursday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.thursday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.tuesday`
    | `scheduledDeliveryConfig.days.tuesday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.tuesday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.days.wednesday`
    | `scheduledDeliveryConfig.days.wednesday.enabledForDelivery`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.from`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.from.hours`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.from.minutes`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.to`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.to.hours`
    | `scheduledDeliveryConfig.days.wednesday.timeSlots.${number}.to.minutes`
    | `scheduledDeliveryConfig.handlingDays`,
    8
  >[]
): Promise<
  NonNullablePaths<
    BulkUpdateLocalDeliveryOptionResponse,
    | `localDeliveryOptions`
    | `localDeliveryOptions.${number}.radiusConfig.radius`
    | `localDeliveryOptions.${number}.radiusConfig.lengthUnit`
    | `errors`
    | `errors.${number}._id`
    | `errors.${number}.error`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      localDeliveryOptions: localDeliveryOptions,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'localDeliveryOptions.radiusConfig.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.bulkUpdateLocalDeliveryOption(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOptions.radiusConfig.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { localDeliveryOptions: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['localDeliveryOptions']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/** @public
 * @documentationMaturity preview
 * @requiredField ids
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_DELETE
 * @applicableIdentity APP
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.BulkDeleteLocalDeliveryOption
 */
export async function bulkDeleteLocalDeliveryOption(
  ids: string[]
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ ids: ids });

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.bulkDeleteLocalDeliveryOption(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { ids: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['ids']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/** @public
 * @documentationMaturity preview
 * @requiredField deliveryRegionId
 * @requiredField options.localDeliveryOptionId
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_UPDATE
 * @applicableIdentity APP
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.AddDeliveryRegion
 */
export async function addDeliveryRegion(
  deliveryRegionId: string,
  options?: NonNullablePaths<
    AddDeliveryRegionOptions,
    `localDeliveryOptionId`,
    2
  >
): Promise<
  NonNullablePaths<
    AddDeliveryRegionResponse,
    | `localDeliveryOption.zipConfig.zipCodes`
    | `localDeliveryOption.radiusConfig.radius`
    | `localDeliveryOption.radiusConfig.lengthUnit`
    | `localDeliveryOption.customAreaConfig.geocodes`
    | `localDeliveryOption.cityConfig.cityItems`
    | `localDeliveryOption.cityConfig.cityItems.${number}.cityName`
    | `localDeliveryOption.cityConfig.cityItems.${number}.country`
    | `localDeliveryOption.destinations`
    | `localDeliveryOption.destinations.${number}.code`
    | `localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots`
    | `localDeliveryOption.scheduledDeliveryConfig.timeWindows`
    | `localDeliveryOption.scheduledDeliveryConfig.timeWindows.${number}.day`
    | `localDeliveryOption.scheduledDeliveryConfig.timeWindows.${number}.start.hours`
    | `localDeliveryOption.scheduledDeliveryConfig.timeWindows.${number}.start.minutes`
    | `localDeliveryOption.rates`
    | `localDeliveryOption.rates.${number}.amount`
    | `localDeliveryOption.rates.${number}.multiplyByQuantity`
    | `localDeliveryOption.deliveryRegionIds`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    deliveryRegionId: deliveryRegionId,
    localDeliveryOptionId: options?.localDeliveryOptionId,
    revision: options?.revision,
  });

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.addDeliveryRegion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOption.radiusConfig.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          deliveryRegionId: '$[0]',
          localDeliveryOptionId: '$[1].localDeliveryOptionId',
          revision: '$[1].revision',
        },
        singleArgumentUnchanged: false,
      },
      ['deliveryRegionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddDeliveryRegionOptions {
  /** @format GUID */
  localDeliveryOptionId: string;
  /** Revision of the PickupLocation */
  revision?: string | null;
}

/** @public
 * @documentationMaturity preview
 * @requiredField deliveryRegionId
 * @requiredField options.localDeliveryOptionId
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_UPDATE
 * @applicableIdentity APP
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.RemoveDeliveryRegion
 */
export async function removeDeliveryRegion(
  deliveryRegionId: string,
  options?: NonNullablePaths<
    RemoveDeliveryRegionOptions,
    `localDeliveryOptionId`,
    2
  >
): Promise<
  NonNullablePaths<
    RemoveDeliveryRegionResponse,
    | `localDeliveryOption.zipConfig.zipCodes`
    | `localDeliveryOption.radiusConfig.radius`
    | `localDeliveryOption.radiusConfig.lengthUnit`
    | `localDeliveryOption.customAreaConfig.geocodes`
    | `localDeliveryOption.cityConfig.cityItems`
    | `localDeliveryOption.cityConfig.cityItems.${number}.cityName`
    | `localDeliveryOption.cityConfig.cityItems.${number}.country`
    | `localDeliveryOption.destinations`
    | `localDeliveryOption.destinations.${number}.code`
    | `localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots`
    | `localDeliveryOption.scheduledDeliveryConfig.timeWindows`
    | `localDeliveryOption.scheduledDeliveryConfig.timeWindows.${number}.day`
    | `localDeliveryOption.scheduledDeliveryConfig.timeWindows.${number}.start.hours`
    | `localDeliveryOption.scheduledDeliveryConfig.timeWindows.${number}.start.minutes`
    | `localDeliveryOption.rates`
    | `localDeliveryOption.rates.${number}.amount`
    | `localDeliveryOption.rates.${number}.multiplyByQuantity`
    | `localDeliveryOption.deliveryRegionIds`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    deliveryRegionId: deliveryRegionId,
    localDeliveryOptionId: options?.localDeliveryOptionId,
    revision: options?.revision,
  });

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.removeDeliveryRegion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOption.radiusConfig.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          deliveryRegionId: '$[0]',
          localDeliveryOptionId: '$[1].localDeliveryOptionId',
          revision: '$[1].revision',
        },
        singleArgumentUnchanged: false,
      },
      ['deliveryRegionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RemoveDeliveryRegionOptions {
  /** @format GUID */
  localDeliveryOptionId: string;
  /** Revision of the PickupLocation */
  revision?: string | null;
}
